import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Progress, Space, Button, Divider, Row, Col, Badge } from 'antd'
import { CloseOutlined, ProjectOutlined, ClockCircleOutlined, CheckCircleOutlined, 
         PauseCircleOutlined, ExclamationCircleOutlined, FireOutlined, BarChartOutlined } from '@ant-design/icons'
import { PlanogramPlan, PlanFilters } from '../../../types/plan'
import dayjs from 'dayjs'
import './StatisticsPanel.css'

interface StatisticsPanelProps {
  currentMonth: Date
  plans: PlanogramPlan[]
  filters?: PlanFilters
  onClose: () => void
}

const StatisticsPanel: React.FC<StatisticsPanelProps> = ({
  currentMonth,
  plans,
  filters = {},
  onClose
}) => {
  const { t } = useTranslation()
  // Filter plans for current month and apply additional filters
  const monthPlans = useMemo(() => {
    const monthStart = dayjs(currentMonth).startOf('month')
    const monthEnd = dayjs(currentMonth).endOf('month')
    
    let filteredPlans = plans.filter(plan => {
      const planStart = dayjs(plan.plannedStartTime)
      const planEnd = dayjs(plan.plannedEndTime)
      
      // Plan overlaps with current month
      return (planStart.isSame(monthEnd) || planStart.isBefore(monthEnd)) && 
             (planEnd.isSame(monthStart) || planEnd.isAfter(monthStart))
    })

    // Apply additional filters for real-time updates
    if (filters.planType) {
      filteredPlans = filteredPlans.filter(plan => plan.planType === filters.planType)
    }
    if (filters.status) {
      filteredPlans = filteredPlans.filter(plan => plan.status === filters.status)
    }
    if (filters.templateReference) {
      filteredPlans = filteredPlans.filter(plan => plan.templateReference === filters.templateReference)
    }
    if (filters.templateOwner) {
      filteredPlans = filteredPlans.filter(plan => 
        plan.templateOwner.toLowerCase().includes(filters.templateOwner!.toLowerCase())
      )
    }
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase()
      filteredPlans = filteredPlans.filter(plan => 
        plan.planName.toLowerCase().includes(keyword) ||
        plan.planCode.toLowerCase().includes(keyword)
      )
    }

    return filteredPlans
  }, [plans, currentMonth, filters])

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalPlans = monthPlans.length
    
    // Plans by status
    const plansByStatus = monthPlans.reduce((acc, plan) => {
      acc[plan.status] = (acc[plan.status] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // Plans by type
    const plansByType = monthPlans.reduce((acc, plan) => {
      acc[plan.planType] = (acc[plan.planType] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // Calculate percentages for type distribution
    const typeDistribution = Object.entries(plansByType).map(([type, count]) => ({
      type,
      count,
      percentage: totalPlans > 0 ? Math.round((count / totalPlans) * 100) : 0
    }))

    return {
      totalPlans,
      plansByStatus,
      plansByType,
      typeDistribution
    }
  }, [monthPlans])

  // Get status color
  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      'Pending': 'default',
      'InProgress': 'processing',
      'Completed': 'success',
      'Paused': 'warning',
      'Cancelled': 'error'
    }
    return colorMap[status] || 'default'
  }

  // Get status text
  const getStatusText = (status: string): string => {
    const textMap: Record<string, string> = {
      'Pending': t('planManagement.list.status.Pending'),
      'InProgress': t('planManagement.list.status.InProgress'),
      'Completed': t('planManagement.list.status.Completed'),
      'Paused': t('planManagement.list.status.Paused'),
      'Cancelled': t('planManagement.list.status.Cancelled')
    }
    return textMap[status] || status
  }

  // Get plan type color
  const getPlanTypeColor = (planType: string): string => {
    const colorMap: Record<string, string> = {
      'Major': 'red',
      'Regular': 'blue',
      'Minor': 'green',
      'Urgent': 'orange',
      'Special': 'purple'
    }
    return colorMap[planType] || 'default'
  }

  // Get plan type text
  const getPlanTypeText = (planType: string): string => {
    const textMap: Record<string, string> = {
      'Major': t('planManagement.list.planTypes.Major'),
      'Regular': t('planManagement.list.planTypes.Regular'),
      'Minor': t('planManagement.list.planTypes.Minor'),
      'Urgent': t('planManagement.list.planTypes.Urgent'),
      'Special': t('planManagement.list.planTypes.Special')
    }
    return textMap[planType] || planType
  }

  return (
    <div className="statistics-panel">
      <Card
        title={
          <Space>
            <BarChartOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontWeight: 600 }}>{t('planManagement.statistics.title', { month: dayjs(currentMonth).format('YYYY/MM') })}</span>
          </Space>
        }
        extra={
          <Button 
            type="text" 
            icon={<CloseOutlined />} 
            onClick={onClose}
            size="small"
            style={{ color: '#666' }}
          />
        }
        size="small"
        style={{ 
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e8f4fd'
        }}
      >
        {/* Key Metrics Dashboard */}
        <div className="stats-gradient-primary">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>
                  {statistics.totalPlans}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  <ProjectOutlined className="stats-icon" style={{ marginRight: '4px' }} />
                  {t('planManagement.statistics.totalPlans')}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Status Overview with Icons */}
        <div style={{ marginBottom: '16px' }}>
          <h4 className="stats-section-header">
            <CheckCircleOutlined className="stats-icon" style={{ marginRight: '6px', color: '#52c41a' }} />
            {t('planManagement.statistics.statusOverview')}
          </h4>
          <Row gutter={[12, 12]}>
            <Col span={6}>
              <div style={{ 
                textAlign: 'center', 
                padding: '12px 8px',
                backgroundColor: '#e6f7ff',
                borderRadius: '8px',
                border: '1px solid #91d5ff'
              }}>
                <ClockCircleOutlined style={{ fontSize: '16px', color: '#1890ff', marginBottom: '4px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff', marginBottom: '2px' }}>
                  {statistics.plansByStatus['InProgress'] || 0}
                </div>
                <div style={{ fontSize: '11px', color: '#1890ff' }}>{t('planManagement.statistics.metrics.inProgress')}</div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ 
                textAlign: 'center', 
                padding: '12px 8px',
                backgroundColor: '#f6ffed',
                borderRadius: '8px',
                border: '1px solid #b7eb8f'
              }}>
                <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a', marginBottom: '4px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a', marginBottom: '2px' }}>
                  {statistics.plansByStatus['Completed'] || 0}
                </div>
                <div style={{ fontSize: '11px', color: '#52c41a' }}>{t('planManagement.statistics.metrics.completed')}</div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ 
                textAlign: 'center', 
                padding: '12px 8px',
                backgroundColor: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #d9d9d9'
              }}>
                <ExclamationCircleOutlined style={{ fontSize: '16px', color: '#8c8c8c', marginBottom: '4px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#8c8c8c', marginBottom: '2px' }}>
                  {statistics.plansByStatus['Pending'] || 0}
                </div>
                <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{t('planManagement.statistics.metrics.notStarted')}</div>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ 
                textAlign: 'center', 
                padding: '12px 8px',
                backgroundColor: '#fff7e6',
                borderRadius: '8px',
                border: '1px solid #ffd591'
              }}>
                <PauseCircleOutlined style={{ fontSize: '16px', color: '#fa8c16', marginBottom: '4px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '2px' }}>
                  {statistics.plansByStatus['Paused'] || 0}
                </div>
                <div style={{ fontSize: '11px', color: '#fa8c16' }}>{t('planManagement.statistics.metrics.paused')}</div>
              </div>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Plan Type Distribution with Enhanced Visuals */}
        <div style={{ marginBottom: '16px' }}>
          <h4 className="stats-section-header">
            <BarChartOutlined className="stats-icon" style={{ marginRight: '6px', color: '#722ed1' }} />
            {t('planManagement.statistics.typeDistribution')}
          </h4>
          <Space direction="vertical" style={{ width: '100%' }}>
            {statistics.typeDistribution.map(({ type, count, percentage }) => (
              <div key={type} className="type-distribution-item" style={{ marginBottom: '8px' }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: '6px' }}>
                  <Col>
                    <Space>
                      <Badge 
                        className="stats-badge"
                        color={getPlanTypeColor(type)} 
                        style={{ marginRight: '4px' }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 500 }}>
                        {getPlanTypeText(type)}
                      </span>
                      {type === 'Urgent' && <FireOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />}
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{count}</span>
                      <span style={{ fontSize: '11px', color: '#666' }}>({percentage}%)</span>
                    </Space>
                  </Col>
                </Row>
                <Progress 
                  percent={percentage} 
                  size="small" 
                  strokeColor={{
                    '0%': getPlanTypeColor(type),
                    '100%': getPlanTypeColor(type),
                  }}
                  trailColor="#f0f0f0"
                  showInfo={false}
                  style={{ marginBottom: '4px' }}
                />
              </div>
            ))}
          </Space>
        </div>
      </Card>
    </div>
  )
}

export default StatisticsPanel